import React, { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, Input, Modal, Select, Space, Table, Tag } from 'antd';
import { message } from '../../utils/antdMessage';
import dayjs from 'dayjs';

function formatCoord(value) {
  if (value == null) return '-';
  let obj = value;
  if (typeof value === 'string') {
    try { obj = JSON.parse(value); } catch (_) { return value; }
  }
  if (typeof obj !== 'object') return String(obj);
  const lat = obj.lat ?? obj.latitude;
  const lng = obj.lng ?? obj.longitude ?? obj.lon;
  if (lat == null || lng == null) return JSON.stringify(obj);
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return `${lat}, ${lng}`;
  return `${la.toFixed(6)}, ${ln.toFixed(6)}`;
}

/** 坐标转换历史管理面板（自包含状态） */
function ConversionHistoryManagePanel({ apiRequest }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState({
    keyword: '',
    from_system: '',
    to_system: '',
    range: null
  });

  const loadData = useCallback(async (nextPage = page, nextSize = pageSize, nextFilters = filters) => {
    if (!apiRequest) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(nextPage));
      params.set('pageSize', String(nextSize));
      if (nextFilters.keyword) params.set('keyword', nextFilters.keyword.trim());
      if (nextFilters.from_system) params.set('from_system', nextFilters.from_system);
      if (nextFilters.to_system) params.set('to_system', nextFilters.to_system);
      if (nextFilters.range?.[0]) {
        params.set('start', nextFilters.range[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'));
      }
      if (nextFilters.range?.[1]) {
        params.set('end', nextFilters.range[1].endOf('day').format('YYYY-MM-DD HH:mm:ss'));
      }
      const data = await apiRequest(`/api/admin/conversion-history?${params.toString()}`);
      if (!data.success) {
        message.error(data.error || '加载转换历史失败');
        return;
      }
      setRows(data.history || []);
      setTotal(data.total || 0);
      setStats(data.stats || null);
      setSelectedKeys([]);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, page, pageSize, filters]);

  useEffect(() => {
    loadData(1, pageSize, filters);
    setPage(1);
    // 仅在挂载时拉取；筛选/分页由按钮与 Table 触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRequest]);

  const handleDeleteOne = (id) => {
    Modal.confirm({
      title: '删除该条转换历史？',
      content: `确定删除记录 #${id}？此操作不可恢复。`,
      okType: 'danger',
      onOk: async () => {
        const data = await apiRequest('/api/admin/conversion-history/delete', {
          method: 'POST',
          body: JSON.stringify({ id })
        });
        if (!data.success) {
          message.error(data.error || '删除失败');
          return;
        }
        message.success('已删除');
        loadData(page, pageSize, filters);
      }
    });
  };

  const handleBatchDelete = () => {
    if (!selectedKeys.length) {
      message.warning('请先勾选要删除的记录');
      return;
    }
    Modal.confirm({
      title: `删除选中的 ${selectedKeys.length} 条记录？`,
      content: '此操作不可恢复。',
      okType: 'danger',
      onOk: async () => {
        const data = await apiRequest('/api/admin/conversion-history/batch-delete', {
          method: 'POST',
          body: JSON.stringify({ ids: selectedKeys })
        });
        if (!data.success) {
          message.error(data.error || '批量删除失败');
          return;
        }
        message.success(`已删除 ${data.deleted || selectedKeys.length} 条`);
        loadData(page, pageSize, filters);
      }
    });
  };

  const handleClearBefore = () => {
    let picked = null;
    Modal.confirm({
      title: '按时间清理历史',
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>删除所选日期<strong>之前</strong>的全部记录：</p>
          <DatePicker
            style={{ width: '100%' }}
            onChange={(v) => { picked = v; }}
            placeholder="选择截止日期"
          />
        </div>
      ),
      okType: 'danger',
      onOk: async () => {
        if (!picked) {
          message.warning('请选择截止日期');
          return Promise.reject();
        }
        const data = await apiRequest('/api/admin/conversion-history/clear', {
          method: 'POST',
          body: JSON.stringify({ before: picked.endOf('day').format('YYYY-MM-DD HH:mm:ss') })
        });
        if (!data.success) {
          message.error(data.error || '清理失败');
          return;
        }
        message.success(`已清理 ${data.deleted || 0} 条`);
        loadData(1, pageSize, filters);
        setPage(1);
      }
    });
  };

  const handleClearAll = () => {
    Modal.confirm({
      title: '清空全部转换历史？',
      content: '将删除所有用户的坐标转换记录，且不可恢复。请确认。',
      okType: 'danger',
      okText: '确认清空',
      onOk: async () => {
        const data = await apiRequest('/api/admin/conversion-history/clear', {
          method: 'POST',
          body: JSON.stringify({ confirm: '确认清空转换历史' })
        });
        if (!data.success) {
          message.error(data.error || '清空失败');
          return;
        }
        message.success(`已清空 ${data.deleted || 0} 条`);
        loadData(1, pageSize, filters);
        setPage(1);
      }
    });
  };

  const systemOptions = ['WGS84', 'GCJ02', 'BD09', 'CGCS2000'];

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>坐标转换历史管理</h3>
      <p style={{ color: '#666', marginBottom: 16 }}>
        管理前台坐标转换产生的历史记录，支持筛选、单条/批量删除与按时间清理。
      </p>

      {stats && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          <Tag color="blue">总记录 {stats.total ?? 0}</Tag>
          <Tag color="green">涉及用户 {stats.user_count ?? 0}</Tag>
          {stats.earliest && <Tag>最早 {dayjs(stats.earliest).format('YYYY-MM-DD')}</Tag>}
          {stats.latest && <Tag>最近 {dayjs(stats.latest).format('YYYY-MM-DD')}</Tag>}
        </div>
      )}

      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          allowClear
          placeholder="用户名/姓名/备注"
          value={filters.keyword}
          onChange={(e) => setFilters((f) => ({ ...f, keyword: e.target.value }))}
          style={{ width: 180 }}
        />
        <Select
          allowClear
          placeholder="源坐标系"
          value={filters.from_system || undefined}
          onChange={(v) => setFilters((f) => ({ ...f, from_system: v || '' }))}
          style={{ width: 120 }}
          options={systemOptions.map((s) => ({ value: s, label: s }))}
        />
        <Select
          allowClear
          placeholder="目标坐标系"
          value={filters.to_system || undefined}
          onChange={(v) => setFilters((f) => ({ ...f, to_system: v || '' }))}
          style={{ width: 120 }}
          options={systemOptions.map((s) => ({ value: s, label: s }))}
        />
        <DatePicker.RangePicker
          value={filters.range}
          onChange={(v) => setFilters((f) => ({ ...f, range: v }))}
        />
        <Button
          type="primary"
          onClick={() => {
            setPage(1);
            loadData(1, pageSize, filters);
          }}
        >
          查询
        </Button>
        <Button danger disabled={!selectedKeys.length} onClick={handleBatchDelete}>
          删除选中 ({selectedKeys.length})
        </Button>
        <Button onClick={handleClearBefore}>按时间清理</Button>
        <Button danger onClick={handleClearAll}>清空全部</Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        scroll={{ x: 1100 }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: setSelectedKeys
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
            loadData(p, s, filters);
          }
        }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 70 },
          {
            title: '用户',
            width: 120,
            render: (_, r) => r.real_name || r.username || (r.user_id ? `#${r.user_id}` : '未知')
          },
          {
            title: '转换',
            width: 130,
            render: (_, r) => `${r.from_system || '?'}→${r.to_system || '?'}`
          },
          {
            title: '原坐标',
            dataIndex: 'original_coordinates',
            width: 180,
            render: formatCoord
          },
          {
            title: '新坐标',
            dataIndex: 'converted_coordinates',
            width: 180,
            render: formatCoord
          },
          { title: '时间', dataIndex: 'timestamp', width: 170 },
          {
            title: '备注',
            dataIndex: 'notes',
            ellipsis: true,
            width: 120,
            render: (t) => t || '-'
          },
          {
            title: '操作',
            key: 'action',
            width: 90,
            fixed: 'right',
            render: (_, record) => (
              <Button type="link" danger onClick={() => handleDeleteOne(record.id)}>
                删除
              </Button>
            )
          }
        ]}
      />
    </div>
  );
}

/** 构建管理后台「转换历史管理」标签页 */
export function buildConversionHistoryTab(context) {
  const { shouldShowPage, apiRequest } = context;
  if (!shouldShowPage('坐标转换历史管理')) return null;

  return {
    key: 'conversion_history',
    label: '转换历史管理',
    children: <ConversionHistoryManagePanel apiRequest={apiRequest} />
  };
}
